import { PHASES_OF_PLAY } from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import type { PitchPositions } from '../components/Pitch';

interface DrillHints {
  format?: string | null;
  name?: string | null;
  primary_phase?: string | null;
  sub_themes?: readonly string[] | null;
}

/**
 * Map a drill's metadata to deterministic pitch positions.
 * Pure function — no randomness. When the drill doesn't match a known
 * pattern, returns null so callers can fall back to Pitch's default layout.
 */
export function layoutForDrill(drill: DrillHints): PitchPositions | null {
  const fmt = inferFormat(drill);
  switch (fmt) {
    case '4v4-wide':
      return WIDE_OVERLOAD_4V4;
    case '8v8-transition':
      return TRANSITION_8V8;
    case '5v3-rondo':
      return RONDO_5V3;
    case 'warmup':
      return WARM_UP_LINE;
    default:
      return null;
  }
}

function inferFormat(d: DrillHints): string | null {
  const phase = toPhaseOfPlay(d.primary_phase);
  const themes = new Set((d.sub_themes ?? []).map((theme) => theme.toLowerCase()));
  const fmt = (d.format ?? '').toLowerCase();
  if (fmt.includes('4v4')) return '4v4-wide';
  if (fmt.includes('8v8')) return '8v8-transition';

  if (
    phase === 'attacking' &&
    (themes.has('width') || themes.has('switching-play') || themes.has('overloads') || themes.has('third-man'))
  ) {
    return '4v4-wide';
  }
  if (
    phase === 'transitions' ||
    themes.has('attack-to-defence') ||
    themes.has('defence-to-attack') ||
    themes.has('turnover-reactions')
  ) {
    return '8v8-transition';
  }
  if (
    phase === 'individual' &&
    (themes.has('first-touch') || themes.has('passing-technique') || themes.has('scanning'))
  ) {
    return 'warmup';
  }
  if (themes.has('decision-making') || themes.has('composure-under-pressure')) return '5v3-rondo';

  const name = (d.name ?? '').toLowerCase();
  if (fmt.includes('4v4') || name.includes('4v4') || name.includes('wide overload')) return '4v4-wide';
  if (fmt.includes('8v8') || name.includes('8v8') || name.includes('transition')) return '8v8-transition';
  if (name.includes('rondo')) return '5v3-rondo';
  if (name.includes('warm')) return 'warmup';
  return null;
}

function toPhaseOfPlay(value: string | null | undefined): PhaseOfPlay | null {
  return PHASES_OF_PLAY.includes(value as PhaseOfPlay) ? value as PhaseOfPlay : null;
}

const WIDE_OVERLOAD_4V4: PitchPositions = {
  home: [
    { x: 70, y: 50 }, { x: 70, y: 150 },
    { x: 130, y: 60 }, { x: 130, y: 140 },
  ],
  away: [
    { x: 220, y: 70 }, { x: 220, y: 130 },
    { x: 270, y: 60 }, { x: 270, y: 140 },
  ],
  ball: { x: 130, y: 100 },
  arrow: { from: { x: 130, y: 100 }, via: { x: 200, y: 70 }, to: { x: 260, y: 100 } },
};

const TRANSITION_8V8: PitchPositions = {
  home: [
    { x: 50, y: 100 },
    { x: 100, y: 60 }, { x: 100, y: 140 },
    { x: 150, y: 80 }, { x: 150, y: 120 },
    { x: 200, y: 50 }, { x: 200, y: 150 },
    { x: 240, y: 100 },
  ],
  away: [
    { x: 220, y: 60 }, { x: 220, y: 140 },
    { x: 260, y: 80 }, { x: 260, y: 120 },
    { x: 300, y: 70 }, { x: 300, y: 130 },
    { x: 320, y: 100 }, { x: 200, y: 100 },
  ],
  ball: { x: 150, y: 100 },
  arrow: { from: { x: 150, y: 100 }, via: { x: 220, y: 70 }, to: { x: 300, y: 90 } },
};

const RONDO_5V3: PitchPositions = {
  home: [
    { x: 80, y: 60 }, { x: 80, y: 140 },
    { x: 120, y: 100 },
    { x: 160, y: 60 }, { x: 160, y: 140 },
  ],
  away: [
    { x: 110, y: 100 }, { x: 130, y: 80 }, { x: 130, y: 120 },
  ],
  ball: { x: 150, y: 100 },
  arrow: { from: { x: 150, y: 100 }, via: { x: 130, y: 60 }, to: { x: 80, y: 70 } },
};

const WARM_UP_LINE: PitchPositions = {
  home: [
    { x: 60, y: 100 }, { x: 110, y: 100 },
    { x: 160, y: 100 }, { x: 210, y: 100 },
    { x: 260, y: 100 }, { x: 310, y: 100 },
  ],
  away: [],
  ball: { x: 60, y: 100 },
  arrow: { from: { x: 60, y: 100 }, to: { x: 310, y: 100 } },
};
